import React from 'react';
import { Helmet } from 'react-helmet';

const SITE_NAME = 'GA4.so';
const BASE_URL = 'https://www.ga4.so';
const DEFAULT_IMAGE = `${BASE_URL}/icon-512.svg`;

/**
 * Reusable SEO component using react-helmet.
 * Provides per-page title, description, Open Graph, Twitter Cards, canonical URL, and optional JSON-LD.
 *
 * @param {string} title - Page title (will be suffixed with " | GA4.so" unless `rawTitle` is true)
 * @param {string} description - Meta description (max ~155 chars recommended)
 * @param {string} path - Route path, e.g. "/prompt-library" (used for canonical + og:url)
 * @param {string} [image] - OG/Twitter image URL (defaults to icon-512.svg)
 * @param {string} [type] - og:type (defaults to "website")
 * @param {boolean} [noindex] - If true, adds noindex meta
 * @param {boolean} [rawTitle] - If true, uses title as-is without suffix
 * @param {object} [jsonLd] - Optional JSON-LD structured data object
 */
export default function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  rawTitle = false,
  jsonLd = null,
}) {
  const fullTitle = rawTitle ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = `${BASE_URL}${path}`;

  return (
    <Helmet>
      {/* Primary Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
